import type { OpenAPI } from "simplydo/interfaces";

import SearchParams from "../searchParams";

export const organisations = (api) => ({
  getActivity(
    id: string,
    data: OpenAPI.POST<"/organisations/{id}/activity">["requestBody"],
    success: (
      data: OpenAPI.POST<"/organisations/{id}/activity">["response"],
    ) => void,
    fail,
  ) {
    api.authenticatedRequest(
      "POST",
      `/organisations/${id}/activity`,
      data,
      success,
      fail,
    );
  },
  getActivityOverview(
    id: string,
    data: OpenAPI.POST<"/organisations/{id}/activity/overview">["requestBody"],
    success: (
      data: OpenAPI.POST<"/organisations/{id}/activity/overview">["response"],
    ) => void,
    fail,
  ) {
    api.authenticatedRequest(
      "POST",
      `/organisations/${id}/activity/overview`,
      data,
      success,
      fail,
    );
  },
  create(
    org: OpenAPI.POST<"/organisations">["requestBody"],
    success: (data: OpenAPI.POST<"/organisations">["response"]) => void,
    fail,
  ) {
    api.authenticatedRequest("POST", "/organisations", org, success, fail);
  },
  get(
    id: string,
    success: (data: OpenAPI.GET<"/organisations/{id}">["response"]) => void,
    fail,
  ) {
    api.authenticatedRequest(
      "GET",
      `/organisations/${id}`,
      null,
      success,
      fail,
    );
  },
  update(
    id: string,
    data: OpenAPI.PUT<"/organisations/{id}">["requestBody"],
    success: (data: OpenAPI.PUT<"/organisations/{id}">["response"]) => void,
    fail,
  ) {
    api.authenticatedRequest(
      "PUT",
      `/organisations/${id}`,
      data,
      success,
      fail,
    );
  },
  delete(
    id: string,
    success: (data: OpenAPI.DELETE<"/organisations/{id}">["response"]) => void,
    fail,
  ) {
    api.authenticatedRequest(
      "DELETE",
      `/organisations/${id}`,
      null,
      success,
      fail,
    );
  },
  block(
    id: string,
    success: (
      data: OpenAPI.POST<"/organisations/{id}/block">["response"],
    ) => void,
    fail,
  ) {
    api.authenticatedRequest(
      "POST",
      `/organisations/${id}/block`,
      null,
      success,
      fail,
    );
  },
  unblock(
    id: string,
    success: (
      data: OpenAPI.DELETE<"/organisations/{id}/block">["response"],
    ) => void,
    fail,
  ) {
    api.authenticatedRequest(
      "DELETE",
      `/organisations/${id}/block`,
      null,
      success,
      fail,
    );
  },
  getAll(
    params: OpenAPI.GET<"/organisations">["parameters"]["query"],
    success: (data: OpenAPI.GET<"/organisations">["response"]) => void,
    fail,
  ) {
    const queryParams = new SearchParams(params).toSafeString();
    api.authenticatedRequest(
      "GET",
      `/organisations${queryParams}`,
      null,
      (data) => success?.(data),
      fail,
    );
  },
  getByCode(
    code: string,
    success: (
      data: OpenAPI.GET<"/organisations/code/{code}">["response"],
    ) => void,
    fail,
  ) {
    api.unauthenticatedRequest(
      "GET",
      `/organisations/code/${code}`,
      null,
      success,
      fail,
    );
  },
  getByDomain(
    domain: string,
    success: (
      data: OpenAPI.GET<"/organisations/domain/{domain}">["response"],
    ) => void,
    fail,
  ) {
    api.unauthenticatedRequest(
      "GET",
      `/organisations/domain/${domain}`,
      null,
      success,
      fail,
    );
  },
  updateSettings(
    id: string,
    data: OpenAPI.PUT<"/organisations/{id}/settings">["requestBody"],
    success: (
      data: OpenAPI.PUT<"/organisations/{id}/settings">["response"],
    ) => void,
    fail,
  ) {
    api.authenticatedRequest(
      "PUT",
      `/organisations/${id}/settings`,
      data,
      success,
      fail,
    );
  },
  getOverview(
    id: string,
    success: (
      data: OpenAPI.GET<"/organisations/{id}/overview">["response"],
    ) => void,
    fail,
  ) {
    api.authenticatedRequest(
      "GET",
      `/organisations/${id}/overview`,
      null,
      success,
      fail,
    );
  },
  getChallengesOverview(
    id: string,
    params: OpenAPI.GET<"/organisations/{id}/overview/challenges">["parameters"]["query"],
    success: (
      data: OpenAPI.GET<"/organisations/{id}/overview/challenges">["response"],
    ) => void,
    fail,
  ) {
    const queryParams = new SearchParams(params).toSafeString();
    api.authenticatedRequest(
      "GET",
      `/organisations/${id}/overview/challenges${queryParams}`,
      null,
      success,
      fail,
    );
  },
  getAdmins(
    id: string,
    params: OpenAPI.GET<"/organisations/{id}/admins">["parameters"]["query"],
    success: (
      data: OpenAPI.GET<"/organisations/{id}/admins">["response"],
    ) => void,
    fail,
  ) {
    const queryParams = new SearchParams(params).toSafeString();
    api.authenticatedRequest(
      "GET",
      `/organisations/${id}/admins${queryParams}`,
      null,
      success,
      fail,
    );
  },
  contactAdmin(
    id: string,
    data: OpenAPI.POST<"/organisations/{id}/admins">["requestBody"],
    success: (
      data: OpenAPI.POST<"/organisations/{id}/admins">["response"],
    ) => void,
    fail,
  ) {
    api.authenticatedRequest(
      "POST",
      `/organisations/${id}/admins`,
      data,
      success,
      fail,
    );
  },
  getUsers(
    id: string,
    params: OpenAPI.GET<"/organisations/{id}/users">["parameters"]["query"],
    success: (
      data: OpenAPI.GET<"/organisations/{id}/users">["response"],
    ) => void,
    fail,
  ) {
    const queryParams = new SearchParams(params).toSafeString();
    api.authenticatedRequest(
      "GET",
      `/organisations/${id}/users${queryParams}`,
      null,
      (data) => success?.(data),
      fail,
    );
  },
  updateFeatureFlags(
    id: string,
    featureFlags: OpenAPI.PUT<"/organisations/{id}/users/features">["requestBody"],
    success: (
      data: OpenAPI.PUT<"/organisations/{id}/users/features">["response"],
    ) => void,
    fail,
  ) {
    api.authenticatedRequest(
      "PUT",
      `/organisations/${id}/users/features`,
      { featureFlags },
      success,
      fail,
    );
  },
  exportUsersToCsv(
    id: string,
    success: (
      data: OpenAPI.GET<"/organisations/{id}/users/export">["response"],
    ) => void,
    fail,
  ) {
    api.authenticatedRequest(
      "GET",
      `/organisations/${id}/users/export`,
      null,
      success,
      fail,
    );
  },
  getChallenges(
    id: string,
    params: OpenAPI.GET<"/organisations/{id}/challenges">["parameters"]["query"],
    success: (
      data: OpenAPI.GET<"/organisations/{id}/challenges">["response"],
    ) => void,
    fail,
  ) {
    const queryParams = new SearchParams(params).toSafeString();
    api.authenticatedRequest(
      "GET",
      `/organisations/${id}/challenges${queryParams}`,
      null,
      (data) => success?.(data.challenges),
      fail,
    );
  },
  getUploads(
    id: string,
    success: (
      data: OpenAPI.GET<"/organisations/{id}/uploads">["response"],
    ) => void,
    fail,
  ) {
    api.authenticatedRequest(
      "GET",
      `/organisations/${id}/uploads`,
      null,
      (data) => success?.(data.uploads),
      fail,
    );
  },
  getStockUploads(
    id: string,
    success: (
      data: OpenAPI.GET<"/organisations/{id}/uploads/stock">["response"],
    ) => void,
    fail,
  ) {
    api.authenticatedRequest(
      "GET",
      `/organisations/${id}/uploads/stock`,
      null,
      (data) => success?.(data.uploads),
      fail,
    );
  },
  createUpload(
    id: string,
    data: OpenAPI.POST<"/organisations/{id}/uploads">["requestBody"],
    success: (
      data: OpenAPI.POST<"/organisations/{id}/uploads">["response"],
    ) => void,
    fail,
  ) {
    api.authenticatedRequest(
      "POST",
      `/organisations/${id}/uploads`,
      data,
      success,
      fail,
    );
  },
  setAsStockImage(
    id: string,
    uploadId: string,
    set: boolean,
    success: (
      data: OpenAPI.PUT<"/organisations/{id}/uploads/{upload_id}/stock">["response"],
    ) => void,
    fail,
  ) {
    api.authenticatedRequest(
      set ? "PUT" : "DELETE",
      `/organisations/${id}/uploads/${uploadId}/stock`,
      null,
      success,
      fail,
    );
  },
  deleteUpload(
    id: string,
    uploadId: string,
    success: (
      data: OpenAPI.DELETE<"/organisations/{id}/uploads/{upload_id}">["response"],
    ) => void,
    fail,
  ) {
    api.authenticatedRequest(
      "DELETE",
      `/organisations/${id}/uploads/${uploadId}`,
      null,
      success,
      fail,
    );
  },
  getStandardAuthSettings(
    id: string,
    success: (
      data: OpenAPI.GET<"/organisations/{id}/auth/standard">["response"],
    ) => void,
    fail,
  ) {
    api.authenticatedRequest(
      "GET",
      `/organisations/${id}/auth/standard`,
      null,
      success,
      fail,
    );
  },
  updateStandardAuthSettings(
    id: string,
    data: OpenAPI.PUT<"/organisations/{id}/auth/standard">["requestBody"],
    success: (
      data: OpenAPI.PUT<"/organisations/{id}/auth/standard">["response"],
    ) => void,
    fail,
  ) {
    api.authenticatedRequest(
      "PUT",
      `/organisations/${id}/auth/standard`,
      data,
      success,
      fail,
    );
  },
  getSamlSettings(
    id: string,
    success: (
      data: OpenAPI.GET<"/organisations/{id}/auth/saml">["response"],
    ) => void,
    fail,
  ) {
    api.authenticatedRequest(
      "GET",
      `/organisations/${id}/auth/saml`,
      null,
      success,
      fail,
    );
  },
  updateSamlSettings(
    id: string,
    data: OpenAPI.PUT<"/organisations/{id}/auth/saml">["requestBody"],
    success: (
      data: OpenAPI.PUT<"/organisations/{id}/auth/saml">["response"],
    ) => void,
    fail,
  ) {
    api.authenticatedRequest(
      "PUT",
      `/organisations/${id}/auth/saml`,
      data,
      success,
      fail,
    );
  },
  getMicrosoftSettings(
    id: string,
    success: (
      data: OpenAPI.GET<"/organisations/{id}/auth/microsoft">["response"],
    ) => void,
    fail,
  ) {
    api.authenticatedRequest(
      "GET",
      `/organisations/${id}/auth/microsoft`,
      null,
      success,
      fail,
    );
  },
  updateMicrosoftSettings(
    id: string,
    data: OpenAPI.PUT<"/organisations/{id}/auth/microsoft">["requestBody"],
    success: (
      data: OpenAPI.PUT<"/organisations/{id}/auth/microsoft">["response"],
    ) => void,
    fail,
  ) {
    api.authenticatedRequest(
      "PUT",
      `/organisations/${id}/auth/microsoft`,
      data,
      success,
      fail,
    );
  },
  getAuthTypes(
    id: string,
    success: (
      data: OpenAPI.GET<"/organisations/{id}/auth/types">["response"],
    ) => void,
    fail,
  ) {
    api.authenticatedRequest(
      "GET",
      `/organisations/${id}/auth/types`,
      null,
      success,
      fail,
    );
  },
  updateAuthTypes(
    id: string,
    types: OpenAPI.PUT<"/organisations/{id}/auth/types">["requestBody"],
    success: (
      data: OpenAPI.PUT<"/organisations/{id}/auth/types">["response"],
    ) => void,
    fail,
  ) {
    api.authenticatedRequest(
      "PUT",
      `/organisations/${id}/auth/types`,
      { types },
      success,
      fail,
    );
  },
  getPolls(
    id: string,
    params: OpenAPI.GET<"/organisations/{id}/polls">["parameters"]["query"],
    success: (
      data: OpenAPI.GET<"/organisations/{id}/polls">["response"],
    ) => void,
    fail,
  ) {
    const queryParams = new SearchParams(params).toSafeString();
    api.authenticatedRequest(
      "GET",
      `/organisations/${id}/polls${queryParams}`,
      null,
      success,
      fail,
    );
  },
  createPoll(
    id: string,
    data: OpenAPI.POST<"/organisations/{id}/polls">["requestBody"],
    success: (
      data: OpenAPI.POST<"/organisations/{id}/polls">["response"],
    ) => void,
    fail,
  ) {
    api.authenticatedRequest(
      "POST",
      `/organisations/${id}/polls`,
      data,
      success,
      fail,
    );
  },
  getDynamics365Connection(
    id: string,
    success: (
      data: OpenAPI.GET<"/organisations/{id}/dynamics365">["response"],
    ) => void,
    fail,
  ) {
    api.authenticatedRequest(
      "GET",
      `/organisations/${id}/dynamics365`,
      null,
      success,
      fail,
    );
  },
  updateDynamics365Connection(
    id: string,
    data: OpenAPI.PUT<"/organisations/{id}/dynamics365">["requestBody"],
    success: (
      data: OpenAPI.PUT<"/organisations/{id}/dynamics365">["response"],
    ) => void,
    fail,
  ) {
    api.authenticatedRequest(
      "PUT",
      `/organisations/${id}/dynamics365`,
      data,
      success,
      fail,
    );
  },
  getDynamics365Companies(
    id: string,
    success: (
      data: OpenAPI.GET<"/organisations/{id}/dynamics365/companies">["response"],
    ) => void,
    fail,
  ) {
    api.authenticatedRequest(
      "GET",
      `/organisations/${id}/dynamics365/companies`,
      null,
      success,
      fail,
    );
  },
  getDynamics365Customers(
    id: string,
    page: number,
    success: (
      data: OpenAPI.GET<"/organisations/{id}/dynamics365/customers">["response"],
    ) => void,
    fail,
  ) {
    const queryParams = new SearchParams({ page }).toSafeString();
    api.authenticatedRequest(
      "GET",
      `/organisations/${id}/dynamics365/customers${queryParams}`,
      null,
      success,
      fail,
    );
  },
  getADConnection(
    id: string,
    success: (
      data: OpenAPI.GET<"/organisations/{id}/activedirectory">["response"],
    ) => void,
    fail,
  ) {
    api.authenticatedRequest(
      "GET",
      `/organisations/${id}/activedirectory`,
      null,
      success,
      fail,
    );
  },
  updateADConnection(
    id: string,
    data: OpenAPI.PUT<"/organisations/{id}/activedirectory">["requestBody"],
    success: (
      data: OpenAPI.PUT<"/organisations/{id}/activedirectory">["response"],
    ) => void,
    fail,
  ) {
    api.authenticatedRequest(
      "PUT",
      `/organisations/${id}/activedirectory`,
      data,
      success,
      fail,
    );
  },
  getPowerbiConnection(
    id: string,
    success: (
      data: OpenAPI.GET<"/organisations/{id}/powerbi">["response"],
    ) => void,
    fail,
  ) {
    api.authenticatedRequest(
      "GET",
      `/organisations/${id}/powerbi`,
      null,
      success,
      fail,
    );
  },
  getPowerbiWorkspaces(
    id: string,
    success: (
      data: OpenAPI.GET<"/organisations/{id}/powerbi/workspaces">["response"],
    ) => void,
    fail,
  ) {
    api.authenticatedRequest(
      "GET",
      `/organisations/${id}/powerbi/workspaces`,
      null,
      success,
      fail,
    );
  },
  createPowerbiDataset(
    id: string,
    datasetType: "report" | "dashboard",
    workspaceId: string,
    workspaceName: string,
    success: (
      data: OpenAPI.POST<"/organisations/{id}/powerbi/datasets">["response"],
    ) => void,
    fail,
  ) {
    api.authenticatedRequest(
      "POST",
      `/organisations/${id}/powerbi/datasets`,
      { datasetType, workspaceId, workspaceName },
      success,
      fail,
    );
  },
  syncPowerbiDataset(
    id: string,
    datasetId: string,
    success: (
      data: OpenAPI.POST<"/organisations/{id}/powerbi/datasets/{dataset_id}">["response"],
    ) => void,
    fail,
  ) {
    api.authenticatedRequest(
      "POST",
      `/organisations/${id}/powerbi/datasets/${datasetId}`,
      null,
      success,
      fail,
    );
  },
  deletePowerbiDataset(
    id: string,
    datasetId: string,
    success: (
      data: OpenAPI.DELETE<"/organisations/{id}/powerbi/datasets/{dataset_id}">["response"],
    ) => void,
    fail,
  ) {
    api.authenticatedRequest(
      "DELETE",
      `/organisations/${id}/powerbi/datasets/${datasetId}`,
      null,
      success,
      fail,
    );
  },
  getSmtpSettings(
    id: string,
    success: (
      data: OpenAPI.GET<"/organisations/{id}/smtp">["response"],
    ) => void,
    fail,
  ) {
    api.authenticatedRequest(
      "GET",
      `/organisations/${id}/smtp`,
      null,
      success,
      fail,
    );
  },
  updateSmtpSettings(
    id: string,
    data: OpenAPI.PUT<"/organisations/{id}/smtp">["requestBody"],
    success: (
      data: OpenAPI.PUT<"/organisations/{id}/smtp">["response"],
    ) => void,
    fail,
  ) {
    api.authenticatedRequest(
      "PUT",
      `/organisations/${id}/smtp`,
      data,
      success,
      fail,
    );
  },
  createDashboard(
    id: string,
    name: string,
    success: (
      data: OpenAPI.POST<"/organisations/{id}/dashboards">["response"],
    ) => void,
    fail,
  ) {
    api.authenticatedRequest(
      "POST",
      `/organisations/${id}/dashboards`,
      { name },
      success,
      fail,
    );
  },
  getDashboards(
    id: string,
    success: (
      data: OpenAPI.GET<"/organisations/{id}/dashboards">["response"],
    ) => void,
    fail,
  ) {
    api.authenticatedRequest(
      "GET",
      `/organisations/${id}/dashboards`,
      null,
      (data) => success?.(data.dashboards),
      fail,
    );
  },
  getDashboard(
    id: string,
    dashboardId: string,
    success: (
      data: OpenAPI.GET<"/organisations/{id}/dashboards/{dashboard_id}">["response"],
    ) => void,
    fail,
  ) {
    api.authenticatedRequest(
      "GET",
      `/organisations/${id}/dashboards/${dashboardId}`,
      null,
      success,
      fail,
    );
  },
  updateDashboard(
    id: string,
    dashboardId: string,
    data: OpenAPI.PUT<"/organisations/{id}/dashboards/{dashboard_id}">["requestBody"],
    success: (
      data: OpenAPI.PUT<"/organisations/{id}/dashboards/{dashboard_id}">["response"],
    ) => void,
    fail,
  ) {
    api.authenticatedRequest(
      "PUT",
      `/organisations/${id}/dashboards/${dashboardId}`,
      data,
      success,
      fail,
    );
  },
  deleteDashboard(
    id: string,
    dashboardId: string,
    success: (
      data: OpenAPI.DELETE<"/organisations/{id}/dashboards/{dashboard_id}">["response"],
    ) => void,
    fail,
  ) {
    api.authenticatedRequest(
      "DELETE",
      `/organisations/${id}/dashboards/${dashboardId}`,
      null,
      success,
      fail,
    );
  },
  createShortenedUrl(
    id: string,
    data: OpenAPI.POST<"/organisations/{id}/shortenedUrls">["requestBody"],
    success: (
      data: OpenAPI.POST<"/organisations/{id}/shortenedUrls">["response"],
    ) => void,
    fail,
  ) {
    api.authenticatedRequest(
      "POST",
      `/organisations/${id}/shortenedUrls`,
      data,
      success,
      fail,
    );
  },
  updateShortenedUrl(
    id: string,
    linkId: string,
    data: OpenAPI.PUT<"/organisations/{id}/shortenedUrls/{url_id}">["requestBody"],
    success: (
      data: OpenAPI.PUT<"/organisations/{id}/shortenedUrls/{url_id}">["response"],
    ) => void,
    fail,
  ) {
    api.authenticatedRequest(
      "PUT",
      `/organisations/${id}/shortenedUrls/${linkId}`,
      data,
      success,
      fail,
    );
  },
  getShortenedUrls(
    id: string,
    params: OpenAPI.GET<"/organisations/{id}/shortenedUrls">["parameters"]["query"],
    success: (
      data: OpenAPI.GET<"/organisations/{id}/shortenedUrls">["response"],
    ) => void,
    fail,
  ) {
    const queryParams = new SearchParams(params).toSafeString();
    api.authenticatedRequest(
      "GET",
      `/organisations/${id}/shortenedUrls${queryParams}`,
      null,
      success,
      fail,
    );
  },
  getShortenedUrl(
    id: string,
    urlId: string,
    success: (
      data: OpenAPI.GET<"/organisations/{id}/shortenedUrls/{url_id}">["response"],
    ) => void,
    fail,
  ) {
    api.authenticatedRequest(
      "GET",
      `/organisations/${id}/shortenedUrls/${urlId}`,
      null,
      success,
      fail,
    );
  },
  getAllShortenedUrls(
    orgId: string,
    success: (
      data: OpenAPI.GET<"/organisations/{id}/shortenedUrls/all">["response"],
    ) => void,
    fail,
  ) {
    api.authenticatedRequest(
      "GET",
      `/organisations/${orgId}/shortenedUrls/all`,
      null,
      success,
      fail,
    );
  },
  deleteShortenedUrl(
    id: string,
    urlId: string,
    success: (
      data: OpenAPI.DELETE<"/organisations/{id}/shortenedUrls/{url_id}">["response"],
    ) => void,
    fail,
  ) {
    api.authenticatedRequest(
      "DELETE",
      `/organisations/${id}/shortenedUrls/${urlId}`,
      null,
      success,
      fail,
    );
  },
  searchPotentialShortenedUrls(
    id: string,
    filter: string,
    success: (
      data: OpenAPI.GET<"/organisations/{id}/shortenedUrls/search">["response"],
    ) => void,
    fail,
  ) {
    api.authenticatedRequest(
      "GET",
      `/organisations/${id}/shortenedUrls/search?filter=${filter}`,
      null,
      success,
      fail,
    );
  },
  getGroups(
    id: string,
    success: (
      data: OpenAPI.GET<"/organisations/{id}/groups">["response"],
    ) => void,
    fail,
  ) {
    api.authenticatedRequest(
      "GET",
      `/organisations/${id}/groups`,
      null,
      (data) => success?.(data.groups),
      fail,
    );
  },
  getPostable(
    id: string,
    success: (
      data: OpenAPI.GET<"/organisations/{id}/postable">["response"],
    ) => void,
    fail,
  ) {
    api.authenticatedRequest(
      "GET",
      `/organisations/${id}/postable`,
      null,
      success,
      fail,
    );
  },
  getShopWindow(
    id: string,
    success: (
      data: OpenAPI.GET<"/organisations/{id}/challenges/public">["response"],
    ) => void,
    fail,
  ) {
    api.maybeAuthenticatedRequest(
      "GET",
      `/organisations/${id}/challenges/public`,
      null,
      success,
      fail,
    );
  },
  createRule(
    id: string,
    data: OpenAPI.POST<"/organisations/{id}/rules">["requestBody"],
    success: (
      data: OpenAPI.POST<"/organisations/{id}/rules">["response"],
    ) => void,
    fail,
  ) {
    api.authenticatedRequest(
      "POST",
      `/organisations/${id}/rules`,
      data,
      success,
      fail,
    );
  },
  updateRule(
    id: string,
    ruleId: string,
    data: OpenAPI.PUT<"/organisations/{id}/rules/{rule_id}">["requestBody"],
    success: (
      data: OpenAPI.PUT<"/organisations/{id}/rules/{rule_id}">["response"],
    ) => void,
    fail,
  ) {
    api.authenticatedRequest(
      "PUT",
      `/organisations/${id}/rules/${ruleId}`,
      data,
      success,
      fail,
    );
  },
  getRules(
    id: string,
    success: (
      data: OpenAPI.GET<"/organisations/{id}/rules">["response"],
    ) => void,
    fail,
  ) {
    api.authenticatedRequest(
      "GET",
      `/organisations/${id}/rules`,
      null,
      success,
      fail,
    );
  },
  deleteRule(
    id: string,
    ruleId: string,
    success: (
      data: OpenAPI.DELETE<"/organisations/{id}/rules/{rule_id}">["response"],
    ) => void,
    fail,
  ) {
    api.authenticatedRequest(
      "DELETE",
      `/organisations/${id}/rules/${ruleId}`,
      null,
      success,
      fail,
    );
  },
  runRule(
    id: string,
    ruleId: string,
    success: (
      data: OpenAPI.POST<"/organisations/{id}/rules/{rule_id}/runs">["response"],
    ) => void,
    fail,
  ) {
    api.authenticatedRequest(
      "POST",
      `/organisations/${id}/rules/${ruleId}/runs`,
      null,
      success,
      fail,
    );
  },
  pinnedChallenges(
    id: string,
    success: (
      data: OpenAPI.GET<"/organisations/{id}/challenges/pinned">["response"],
    ) => void,
    fail,
  ) {
    api.authenticatedRequest(
      "GET",
      `/organisations/${id}/challenges/pinned`,
      null,
      success,
      fail,
    );
  },
  homepagePolls(
    id: string,
    success: (
      data: OpenAPI.GET<"/organisations/{id}/polls/pinned">["response"],
    ) => void,
    fail,
  ) {
    api.authenticatedRequest(
      "GET",
      `/organisations/${id}/polls/pinned`,
      null,
      success,
      fail,
    );
  },
  postToActivityFeed(
    id: string,
    data: OpenAPI.POST<"/organisations/{id}/activityfeed">["requestBody"],
    success: (
      data: OpenAPI.POST<"/organisations/{id}/activityfeed">["response"],
    ) => void,
    fail,
  ) {
    api.authenticatedRequest(
      "POST",
      `/organisations/${id}/activityfeed`,
      data,
      success,
      fail,
    );
  },
  updateActivityFeedPost(
    id: string,
    postId: string,
    data: OpenAPI.POST<"/organisations/{id}/activityfeed/{post_id}">["requestBody"],
    success: (
      data: OpenAPI.POST<"/organisations/{id}/activityfeed/{post_id}">["response"],
    ) => void,
    fail,
  ) {
    api.authenticatedRequest(
      "POST",
      `/organisations/${id}/activityfeed/${postId}`,
      data,
      success,
      fail,
    );
  },
  deletePostFromActivityFeed(
    id: string,
    postId: string,
    success: (
      data: OpenAPI.DELETE<"/organisations/{id}/activityfeed/{post_id}">["response"],
    ) => void,
    fail,
  ) {
    api.authenticatedRequest(
      "DELETE",
      `/organisations/${id}/activityfeed/${postId}`,
      null,
      success,
      fail,
    );
  },
  getAchievements(
    id: string,
    success: (
      data: OpenAPI.GET<"/organisations/{id}/achievements">["response"],
    ) => void,
    fail,
  ) {
    api.authenticatedRequest(
      "GET",
      `/organisations/${id}/achievements`,
      null,
      success,
      fail,
    );
  },
  createAchievement(
    id: string,
    data: OpenAPI.PUT<"/organisations/{id}/achievements">["requestBody"],
    success: (
      data: OpenAPI.PUT<"/organisations/{id}/achievements">["response"],
    ) => void,
    fail,
  ) {
    api.authenticatedRequest(
      "PUT",
      `/organisations/${id}/achievements`,
      data,
      success,
      fail,
    );
  },
  updateAchievement(
    id: string,
    achievementId: string,
    data: OpenAPI.POST<"/organisations/{id}/achievements/{achievement_id}">["requestBody"],
    success: (
      data: OpenAPI.POST<"/organisations/{id}/achievements/{achievement_id}">["response"],
    ) => void,
    fail,
  ) {
    api.authenticatedRequest(
      "POST",
      `/organisations/${id}/achievements/${achievementId}`,
      data,
      success,
      fail,
    );
  },
  deleteAchievement(
    id: string,
    achievementId: string,
    success: (
      data: OpenAPI.DELETE<"/organisations/{id}/achievements/{achievement_id}">["response"],
    ) => void,
    fail,
  ) {
    api.authenticatedRequest(
      "DELETE",
      `/organisations/${id}/achievements/${achievementId}`,
      null,
      success,
      fail,
    );
  },
  bulkAwardAchievement(
    id: string,
    achievementId: string,
    data: OpenAPI.PUT<"/organisations/{id}/achievements/{achievement_id}/award">["requestBody"],
    success: (
      data: OpenAPI.PUT<"/organisations/{id}/achievements/{achievement_id}/award">["response"],
    ) => void,
    fail,
  ) {
    api.authenticatedRequest(
      "PUT",
      `/organisations/${id}/achievements/${achievementId}/award`,
      data,
      success,
      fail,
    );
  },
  awardAchievement(
    id: string,
    achievementId: string,
    userId: string,
    success: (
      data: OpenAPI.PUT<"/organisations/{id}/achievements/{achievement_id}/award/{user_id}">["response"],
    ) => void,
    fail,
  ) {
    api.authenticatedRequest(
      "PUT",
      `/organisations/${id}/achievements/${achievementId}/award/${userId}`,
      null,
      success,
      fail,
    );
  },
  removeAwardAchievement(
    id: string,
    achievementId: string,
    userId: string,
    success: (
      data: OpenAPI.DELETE<"/organisations/{id}/achievements/{achievement_id}/award/{user_id}">["response"],
    ) => void,
    fail,
  ) {
    api.authenticatedRequest(
      "DELETE",
      `/organisations/${id}/achievements/${achievementId}/award/${userId}`,
      null,
      success,
      fail,
    );
  },
  getOnboarding(
    id: string,
    success: (
      data: OpenAPI.GET<"/organisations/{id}/onboarding">["response"],
    ) => void,
    fail,
  ) {
    api.authenticatedRequest(
      "GET",
      `/organisations/${id}/onboarding`,
      null,
      success,
      fail,
    );
  },
  getOnboardingTemplates(
    id: string,
    success: (
      data: OpenAPI.GET<"/organisations/{id}/onboarding/templates">["response"],
    ) => void,
    fail,
  ) {
    api.authenticatedRequest(
      "GET",
      `/organisations/${id}/onboarding/templates`,
      null,
      success,
      fail,
    );
  },
  enableOnboarding(
    id: string,
    success: (
      data: OpenAPI.POST<"/organisations/{id}/onboarding">["response"],
    ) => void,
    fail,
  ) {
    api.authenticatedRequest(
      "POST",
      `/organisations/${id}/onboarding`,
      null,
      success,
      fail,
    );
  },
  disableOnboarding(
    id: string,
    success: (
      data: OpenAPI.DELETE<"/organisations/{id}/onboarding">["response"],
    ) => void,
    fail,
  ) {
    api.authenticatedRequest(
      "DELETE",
      `/organisations/${id}/onboarding`,
      null,
      success,
      fail,
    );
  },
  addOnboardingScreen(
    id: string,
    success: (
      data: OpenAPI.PUT<"/organisations/{id}/onboarding/screens">["response"],
    ) => void,
    fail,
  ) {
    api.authenticatedRequest(
      "PUT",
      `/organisations/${id}/onboarding/screens`,
      null,
      success,
      fail,
    );
  },
  removeOnboardingScreen(
    id: string,
    screenId: string,
    success: (
      data: OpenAPI.DELETE<"/organisations/{id}/onboarding/screens/{screen_id}">["response"],
    ) => void,
    fail,
  ) {
    api.authenticatedRequest(
      "DELETE",
      `/organisations/${id}/onboarding/screens/${screenId}`,
      null,
      success,
      fail,
    );
  },
  updateOnboardingScreen(
    id: string,
    screenId: string,
    data: OpenAPI.POST<"/organisations/{id}/onboarding/screens/{screen_id}">["requestBody"],
    success: (
      data: OpenAPI.POST<"/organisations/{id}/onboarding/screens/{screen_id}">["response"],
    ) => void,
    fail,
  ) {
    api.authenticatedRequest(
      "POST",
      `/organisations/${id}/onboarding/screens/${screenId}`,
      data,
      success,
      fail,
    );
  },
  trackOnboardingCompletion(
    id: string,
    success: (
      data: OpenAPI.PUT<"/organisations/{id}/onboarding/seen">["response"],
    ) => void,
    fail,
  ) {
    api.authenticatedRequest(
      "PUT",
      `/organisations/${id}/onboarding/seen`,
      null,
      success,
      fail,
    );
  },
  importOnboardingTemplate(
    id: string,
    templateId: string,
    success: (
      data: OpenAPI.POST<"/organisations/{id}/onboarding/templates/{template_id}">["response"],
    ) => void,
    fail,
  ) {
    api.authenticatedRequest(
      "POST",
      `/organisations/${id}/onboarding/templates/${templateId}`,
      null,
      success,
      fail,
    );
  },
  getGoals(
    id: string,
    success: (
      data: OpenAPI.GET<"/organisations/{id}/goals">["response"],
    ) => void,
    fail,
  ) {
    api.authenticatedRequest(
      "GET",
      `/organisations/${id}/goals`,
      null,
      success,
      fail,
    );
  },
  createGoal(
    id: string,
    data: OpenAPI.POST<"/organisations/{id}/goals">["requestBody"],
    success: (
      data: OpenAPI.POST<"/organisations/{id}/goals">["response"],
    ) => void,
    fail,
  ) {
    api.authenticatedRequest(
      "POST",
      `/organisations/${id}/goals`,
      data,
      success,
      fail,
    );
  },
  updateGoal(
    id: string,
    goalId: string,
    data: OpenAPI.POST<"/organisations/{id}/goals/{goal_id}">["requestBody"],
    success: (
      data: OpenAPI.POST<"/organisations/{id}/goals/{goal_id}">["response"],
    ) => void,
    fail,
  ) {
    api.authenticatedRequest(
      "POST",
      `/organisations/${id}/goals/${goalId}`,
      data,
      success,
      fail,
    );
  },
  deleteGoal(
    id: string,
    goalId: string,
    success: (
      data: OpenAPI.DELETE<"/organisations/{id}/goals/{goal_id}">["response"],
    ) => void,
    fail,
  ) {
    api.authenticatedRequest(
      "DELETE",
      `/organisations/${id}/goals/${goalId}`,
      null,
      success,
      fail,
    );
  },
  getGoalEntityCount(
    id: string,
    entity: string,
    success: (
      data: OpenAPI.GET<"/organisations/{id}/goals/entity/{entity}">["response"],
    ) => void,
    fail,
  ) {
    api.authenticatedRequest(
      "GET",
      `/organisations/${id}/goals/entity/${entity}`,
      null,
      success,
      fail,
    );
  },
  createImpact(
    id: string,
    data: OpenAPI.POST<"/organisations/{id}/impacts">["requestBody"],
    success: (
      data: OpenAPI.POST<"/organisations/{id}/impacts">["response"],
    ) => void,
    fail,
  ) {
    api.authenticatedRequest(
      "POST",
      `/organisations/${id}/impacts`,
      data,
      success,
      fail,
    );
  },
  getImpacts(
    id: string,
    success: (
      data: OpenAPI.GET<"/organisations/{id}/impacts">["response"],
    ) => void,
    fail,
  ) {
    api.authenticatedRequest(
      "GET",
      `/organisations/${id}/impacts`,
      null,
      success,
      fail,
    );
  },
  deleteImpact(
    id: string,
    impactId: string,
    success: (
      data: OpenAPI.DELETE<"/organisations/{id}/impacts/{impact_id}">["response"],
    ) => void,
    fail,
  ) {
    api.authenticatedRequest(
      "DELETE",
      `/organisations/${id}/impacts/${impactId}`,
      null,
      success,
      fail,
    );
  },
  updateImpact(
    id: string,
    impactId: string,
    data: OpenAPI.POST<"/organisations/{id}/impacts/{impact_id}">["requestBody"],
    success: (
      data: OpenAPI.POST<"/organisations/{id}/impacts/{impact_id}">["response"],
    ) => void,
    fail,
  ) {
    api.authenticatedRequest(
      "POST",
      `/organisations/${id}/impacts/${impactId}`,
      data,
      success,
      fail,
    );
  },
  moveImpact(
    id: string,
    impactId: string,
    categoryId: string,
    order: number,
    success: (
      data: OpenAPI.PUT<"/organisations/{id}/impacts/{impact_id}/move">["response"],
    ) => void,
    fail,
  ) {
    api.authenticatedRequest(
      "PUT",
      `/organisations/${id}/impacts/${impactId}/move`,
      { categoryId, order },
      success,
      fail,
    );
  },
  createImpactCategory(
    id: string,
    data: OpenAPI.POST<"/organisations/{id}/impacts/categories">["requestBody"],
    success: (
      data: OpenAPI.POST<"/organisations/{id}/impacts/categories">["response"],
    ) => void,
    fail,
  ) {
    api.authenticatedRequest(
      "POST",
      `/organisations/${id}/impacts/categories`,
      data,
      success,
      fail,
    );
  },
  deleteImpactCategory(
    id: string,
    categoryId: string,
    success: (
      data: OpenAPI.DELETE<"/organisations/{id}/impacts/categories/{category_id}">["response"],
    ) => void,
    fail,
  ) {
    api.authenticatedRequest(
      "DELETE",
      `/organisations/${id}/impacts/categories/${categoryId}`,
      null,
      success,
      fail,
    );
  },
  updateImpactCategory(
    id: string,
    categoryId: string,
    data: OpenAPI.POST<"/organisations/{id}/impacts/categories/{category_id}">["requestBody"],
    success: (
      data: OpenAPI.POST<"/organisations/{id}/impacts/categories/{category_id}">["response"],
    ) => void,
    fail,
  ) {
    api.authenticatedRequest(
      "POST",
      `/organisations/${id}/impacts/categories/${categoryId}`,
      data,
      success,
      fail,
    );
  },
  moveImpactCategory(
    id: string,
    categoryId: string,
    order: number,
    success: (
      data: OpenAPI.PUT<"/organisations/{id}/impacts/categories/{category_id}/move">["response"],
    ) => void,
    fail,
  ) {
    api.authenticatedRequest(
      "PUT",
      `/organisations/${id}/impacts/categories/${categoryId}/move`,
      { order },
      success,
      fail,
    );
  },
  getEmailTemplates(
    id: string,
    success: (
      data: OpenAPI.GET<"/organisations/{org_id}/communication/emails">["response"],
    ) => void,
    fail,
  ) {
    api.authenticatedRequest(
      "GET",
      `/organisations/${id}/communication/emails`,
      null,
      success,
      fail,
    );
  },
  updateEmailTemplate(
    id: string,
    templateName: string,
    data: OpenAPI.POST<"/organisations/{org_id}/communication/emails/{template_name}">["requestBody"],
    success: (
      data: OpenAPI.POST<"/organisations/{org_id}/communication/emails/{template_name}">["response"],
    ) => void,
    fail,
  ) {
    api.authenticatedRequest(
      "POST",
      `/organisations/${id}/communication/emails/${templateName}`,
      data,
      success,
      fail,
    );
  },
  deleteEmailTemplate(
    id: string,
    templateName: string,
    success: (
      data: OpenAPI.DELETE<"/organisations/{org_id}/communication/emails/{template_name}">["response"],
    ) => void,
    fail,
  ) {
    api.authenticatedRequest(
      "DELETE",
      `/organisations/${id}/communication/emails/${templateName}`,
      null,
      success,
      fail,
    );
  },
  getIpAddress(
    id: string,
    success: (
      data: OpenAPI.GET<"/organisations/{id}/settings/validateIpAction">["response"],
    ) => void,
    fail,
  ) {
    api.authenticatedRequest(
      "GET",
      `/organisations/${id}/settings/validateIpAction`,
      null,
      success,
      fail,
    );
  },
  checkValidIpAction(
    id: string,
    data: OpenAPI.PUT<"/organisations/{id}/settings/validateIpAction">["requestBody"],
    success: (
      data: OpenAPI.PUT<"/organisations/{id}/settings/validateIpAction">["response"],
    ) => void,
    fail,
  ) {
    api.authenticatedRequest(
      "PUT",
      `/organisations/${id}/settings/validateIpAction`,
      data,
      success,
      fail,
    );
  },
  exploreIdeasOptions(
    id: string,
    success: (
      data: OpenAPI.GET<"/organisations/{id}/explore/ideas/options">["response"],
    ) => void,
    fail,
  ) {
    api.authenticatedRequest(
      "GET",
      `/organisations/${id}/explore/ideas/options`,
      null,
      success,
      fail,
    );
  },
  exploreIdeas(
    id: string,
    params: OpenAPI.GET<"/organisations/{id}/explore/ideas">["parameters"]["query"],
    success: (
      data: OpenAPI.GET<"/organisations/{id}/explore/ideas">["response"],
    ) => void,
    fail,
  ) {
    const queryParams = new SearchParams(params).toSafeString();
    api.authenticatedRequest(
      "GET",
      `/organisations/${id}/explore/ideas${queryParams}`,
      null,
      success,
      fail,
    );
  },
  getIdeaImpact(
    id: string,
    params: OpenAPI.GET<"/organisations/{id}/impact">["parameters"]["query"],
    success: (
      data: OpenAPI.GET<"/organisations/{id}/impact">["response"],
    ) => void,
    fail,
  ) {
    const queryParams = new SearchParams(params).toSafeString();
    api.authenticatedRequest(
      "GET",
      `/organisations/${id}/impact${queryParams}`,
      null,
      success,
      fail,
    );
  },
  getProductTours(
    id: string,
    success: (
      data: OpenAPI.GET<"/organisations/{id}/producttours">["response"],
    ) => void,
    fail,
  ) {
    api.authenticatedRequest(
      "GET",
      `/organisations/${id}/producttours`,
      null,
      success,
      fail,
    );
  },
  getProductTour(
    id: string,
    tourId: string,
    success: (
      data: OpenAPI.GET<"/organisations/{id}/producttours/{tour_id}">["response"],
    ) => void,
    fail,
  ) {
    api.authenticatedRequest(
      "GET",
      `/organisations/${id}/producttours/${tourId}`,
      null,
      success,
      fail,
    );
  },
  updateProductTour(
    id: string,
    tourId: string,
    data: OpenAPI.POST<"/organisations/{id}/producttours/{tour_id}">["requestBody"],
    success: (
      data: OpenAPI.POST<"/organisations/{id}/producttours/{tour_id}">["response"],
    ) => void,
    fail,
  ) {
    api.authenticatedRequest(
      "POST",
      `/organisations/${id}/producttours/${tourId}`,
      data,
      success,
      fail,
    );
  },
  completeProductTour(
    id: string,
    tourId: string,
    success: (
      data: OpenAPI.POST<"/organisations/{id}/producttours/{tour_id}/complete">["response"],
    ) => void,
    fail,
  ) {
    api.authenticatedRequest(
      "POST",
      `/organisations/${id}/producttours/${tourId}/complete`,
      null,
      success,
      fail,
    );
  },
  resetProductTour(
    id: string,
    tourId: string,
    success: (
      data: OpenAPI.POST<"/organisations/{id}/producttours/{tour_id}/reset">["response"],
    ) => void,
    fail,
  ) {
    api.authenticatedRequest(
      "POST",
      `/organisations/${id}/producttours/${tourId}/reset`,
      null,
      success,
      fail,
    );
  },
  ipScopeInUse(
    id: string,
    scopeId: string,
    success: (
      data: OpenAPI.GET<"/organisations/{id}/ipscopes/{scopeId}">["response"],
    ) => void,
    fail,
  ) {
    api.authenticatedRequest(
      "GET",
      `/organisations/${id}/ipscopes/${scopeId}`,
      null,
      success,
      fail,
    );
  },
  revokeTeamsAdminConsent(
    tenantId: string,
    success: (
      data: OpenAPI.DELETE<"/organisations/tenantId/{tenant_id}/teams/consent">["response"],
    ) => void,
    fail,
  ) {
    api.authenticatedRequest(
      "DELETE",
      `/organisations/tenantId/${tenantId}/teams/consent`,
      null,
      success,
      fail,
    );
  },
  getAvailableProfiles(
    id: string,
    challengeId: string,
    success: (
      data: OpenAPI.GET<"/organisations/{id}/profiles/available">["response"],
    ) => void,
    fail,
  ) {
    api.authenticatedRequest(
      "GET",
      `/organisations/${id}/profiles/available?challenge=${challengeId}`,
      null,
      success,
      fail,
    );
  },
  getProfile(
    orgId: string,
    profileId: string,
    success: (
      data: OpenAPI.GET<"/organisations/{org_id}/profiles/{profile_id}">["response"],
    ) => void,
    fail,
  ) {
    api.authenticatedRequest(
      "GET",
      `/organisations/${orgId}/profiles/${profileId}`,
      null,
      success,
      fail,
    );
  },
  createProfile(
    orgId: string,
    name: string,
    success: (
      data: OpenAPI.POST<"/organisations/{org_id}/profiles">["response"],
    ) => void,
    fail,
  ) {
    api.authenticatedRequest(
      "POST",
      `/organisations/${orgId}/profiles`,
      { name },
      success,
      fail,
    );
  },
  updateProfile(
    orgId: string,
    profileId: string,
    data: OpenAPI.PUT<"/organisations/{org_id}/profiles/{profile_id}">["requestBody"],
    success: (
      data: OpenAPI.PUT<"/organisations/{org_id}/profiles/{profile_id}">["response"],
    ) => void,
    fail,
  ) {
    api.authenticatedRequest(
      "PUT",
      `/organisations/${orgId}/profiles/${profileId}`,
      data,
      success,
      fail,
    );
  },
  deleteProfile(
    orgId: string,
    profileId: string,
    success: (
      data: OpenAPI.DELETE<"/organisations/{org_id}/profiles/{profile_id}">["response"],
    ) => void,
    fail,
  ) {
    api.authenticatedRequest(
      "DELETE",
      `/organisations/${orgId}/profiles/${profileId}`,
      null,
      success,
      fail,
    );
  },
  getLogs(
    orgId: string,
    params: OpenAPI.GET<"/organisations/{org_id}/communication/emails/logs">["parameters"]["query"],
    success: (
      data: OpenAPI.GET<"/organisations/{org_id}/communication/emails/logs">["response"],
    ) => void,
    fail,
  ) {
    const urlParams = new SearchParams(params);
    api.authenticatedRequest(
      "GET",
      `/organisations/${orgId}/communication/emails/logs${urlParams.toSafeString()}`,
      null,
      success,
      fail,
    );
  },
  getLog(
    orgId: string,
    logId: string,
    success: (
      data: OpenAPI.GET<"/organisations/{org_id}/communication/emails/logs/{log_id}">["response"],
    ) => void,
    fail,
  ) {
    api.authenticatedRequest(
      "GET",
      `/organisations/${orgId}/communication/emails/logs/${logId}`,
      null,
      success,
      fail,
    );
  },
  getPointOfContactRoles(
    orgId: string,
    success: (
      data: OpenAPI.GET<"/organisations/{org_id}/contact/roles">["response"],
    ) => void,
    fail,
  ) {
    api.authenticatedRequest(
      "GET",
      `/organisations/${orgId}/contact/roles`,
      null,
      success,
      fail,
    );
  },
  createPointOfContactRole(
    orgId: string,
    data: OpenAPI.POST<"/organisations/{org_id}/contact/roles">["requestBody"],
    success: (
      data: OpenAPI.POST<"/organisations/{org_id}/contact/roles">["response"],
    ) => void,
    fail,
  ) {
    api.authenticatedRequest(
      "POST",
      `/organisations/${orgId}/contact/roles`,
      data,
      success,
      fail,
    );
  },
  updatePointOfContactRole(
    orgId: string,
    roleId: string,
    data: OpenAPI.PUT<"/organisations/{org_id}/contact/roles/{role_id}">["requestBody"],
    success: (
      data: OpenAPI.PUT<"/organisations/{org_id}/contact/roles/{role_id}">["response"],
    ) => void,
    fail,
  ) {
    api.authenticatedRequest(
      "PUT",
      `/organisations/${orgId}/contact/roles/${roleId}`,
      data,
      success,
      fail,
    );
  },
  reorderPointOfContactRole(
    orgId: string,
    roleId: string,
    direction: "up" | "down",
    success: (
      data: OpenAPI.PUT<"/organisations/{org_id}/contact/roles/{role_id}/move/{direction}">["response"],
    ) => void,
    fail,
  ) {
    api.authenticatedRequest(
      "PUT",
      `/organisations/${orgId}/contact/roles/${roleId}/move/${direction}`,
      null,
      success,
      fail,
    );
  },
  deletePointOfContactRole(
    orgId: string,
    roleId: string,
    success: (
      data: OpenAPI.DELETE<"/organisations/{org_id}/contact/roles/{role_id}">["response"],
    ) => void,
    fail,
  ) {
    api.authenticatedRequest(
      "DELETE",
      `/organisations/${orgId}/contact/roles/${roleId}`,
      null,
      success,
      fail,
    );
  },
  assignPointOfContact(
    orgId: string,
    roleId: string,
    userId: string,
    success: (
      data: OpenAPI.PUT<"/organisations/{org_id}/contact/roles/{role_id}/assign/{user_id}">["response"],
    ) => void,
    fail,
  ) {
    api.authenticatedRequest(
      "PUT",
      `/organisations/${orgId}/contact/roles/${roleId}/assign/${userId}`,
      null,
      success,
      fail,
    );
  },
  unassignPointOfContact(
    orgId: string,
    roleId: string,
    userId: string,
    success: (
      data: OpenAPI.DELETE<"/organisations/{org_id}/contact/roles/{role_id}/assign/{user_id}">["response"],
    ) => void,
    fail,
  ) {
    api.authenticatedRequest(
      "DELETE",
      `/organisations/${orgId}/contact/roles/${roleId}/assign/${userId}`,
      null,
      success,
      fail,
    );
  },
  sendBrandingTestEmail(
    orgId: string,
    success: (
      data: OpenAPI.GET<"/organisations/{org_id}/communication/emails/test/branding">["response"],
    ) => void,
    fail,
  ) {
    api.authenticatedRequest(
      "GET",
      `/organisations/${orgId}/communication/emails/test/branding`,
      null,
      success,
      fail,
    );
  },
  sendCustomServerTestEmail(
    orgId: string,
    settings: OpenAPI.POST<"/organisations/{org_id}/communication/emails/test/smtp">["requestBody"],
    success: (
      data: OpenAPI.POST<"/organisations/{org_id}/communication/emails/test/smtp">["response"],
    ) => void,
    fail,
  ) {
    api.authenticatedRequest(
      "POST",
      `/organisations/${orgId}/communication/emails/test/smtp`,
      { settings },
      success,
      fail,
    );
  },
  getBusinesses(
    orgId: string,
    params: OpenAPI.GET<"/organisations/{id}/businesses">["parameters"]["query"],
    success: (
      data: OpenAPI.GET<"/organisations/{id}/businesses">["response"],
    ) => void,
    fail,
  ) {
    const queryParams = new SearchParams(params).toSafeString();
    api.authenticatedRequest(
      "GET",
      `/organisations/${orgId}/businesses${queryParams}`,
      null,
      success,
      fail,
    );
  },
});
